import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

// 扩展dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

/**
 * 格式化UTC时间为本地时间显示
 * @param utcTimeString UTC时间字符串
 * @param format 显示格式，默认为 'YYYY/MM/DD HH:mm:ss'
 * @returns 格式化后的本地时间字符串
 */
export const formatUTCToLocal = (utcTimeString: string, format: string = 'YYYY/MM/DD HH:mm:ss'): string => {
  if (!utcTimeString) return '';
  
  try {
    // 将UTC时间转换为本地时间
    const localTime = dayjs.utc(utcTimeString).local();
    return localTime.format(format);
  } catch (error) {
    console.error('时间格式化失败:', error);
    return utcTimeString;
  }
};

/**
 * 格式化UTC时间为本地时间显示（简化版本）
 * @param utcTimeString UTC时间字符串
 * @returns 格式化后的本地时间字符串，格式为 'YYYY/MM/DD HH:mm'
 */
export const formatUTCToLocalShort = (utcTimeString: string): string => {
  return formatUTCToLocal(utcTimeString, 'YYYY/MM/DD HH:mm');
};

/**
 * 格式化UTC时间为本地日期显示
 * @param utcTimeString UTC时间字符串
 * @returns 格式化后的本地日期字符串，格式为 'YYYY-MM-DD'
 */
export const formatUTCToLocalDate = (utcTimeString: string): string => {
  return formatUTCToLocal(utcTimeString, 'YYYY-MM-DD');
};

/**
 * 获取当前本地时间
 * @param format 显示格式，默认为 'YYYY/MM/DD HH:mm:ss'
 * @returns 格式化后的当前本地时间字符串
 */
export const getCurrentLocalTime = (format: string = 'YYYY/MM/DD HH:mm:ss'): string => {
  return dayjs().format(format);
};

/**
 * 计算时间差（相对时间）
 * @param utcTimeString UTC时间字符串
 * @returns 相对时间描述，如 "2小时前"
 */
export const getRelativeTime = (utcTimeString: string): string => {
  if (!utcTimeString) return '';
  
  try {
    const localTime = dayjs.utc(utcTimeString).local();
    return localTime.fromNow();
  } catch (error) {
    console.error('相对时间计算失败:', error);
    return utcTimeString;
  }
};

/**
 * 检查时间字符串是否为UTC格式
 * @param timeString 时间字符串
 * @returns 是否为UTC格式
 */
export const isUTCTime = (timeString: string): boolean => {
  return timeString.endsWith('Z') || timeString.includes('+00:00') || timeString.includes('UTC');
};

/**
 * 智能时间格式化：自动检测是否为UTC时间并转换
 * @param timeString 时间字符串
 * @param format 显示格式
 * @returns 格式化后的时间字符串
 */
export const smartTimeFormat = (timeString: string, format: string = 'YYYY/MM/DD HH:mm:ss'): string => {
  if (!timeString) return '';
  
  try {
    if (isUTCTime(timeString)) {
      return formatUTCToLocal(timeString, format);
    } else {
      // 假设已经是本地时间
      return dayjs(timeString).format(format);
    }
  } catch (error) {
    console.error('智能时间格式化失败:', error);
    return timeString;
  }
};
